import { useEffect, useState } from 'react';

// Host de toasts global. Cualquier módulo puede pinchar un toast llamando
// a `toast.info/success/warning/error(mensaje, opciones?)`. El componente
// se monta UNA vez en App.tsx (junto a OfflineBanner).
//
// Por qué no usamos window.confirm/alert/prompt: bloquean el hilo, no se
// pueden estilar, y el feedback `feedback_no_browser_confirm` lo prohíbe.

type Kind = 'info' | 'success' | 'warning' | 'error';
interface Toast { id: number; kind: Kind; text: string; ttl: number; }

let nextId = 1;
const listeners = new Set<(t: Toast[]) => void>();
let current: Toast[] = [];

function emit() { for (const l of listeners) l(current); }

function push(kind: Kind, text: string, ttl = 4000) {
  const id = nextId++;
  current = [...current, { id, kind, text, ttl }];
  emit();
  if (ttl > 0) {
    setTimeout(() => {
      current = current.filter(x => x.id !== id);
      emit();
    }, ttl);
  }
  return id;
}

function dismiss(id: number) {
  current = current.filter(x => x.id !== id);
  emit();
}

export const toast = {
  info:    (m: string, ttl?: number) => push('info', m, ttl),
  success: (m: string, ttl?: number) => push('success', m, ttl),
  warning: (m: string, ttl?: number) => push('warning', m, ttl),
  error:   (m: string, ttl?: number) => push('error', m, ttl ?? 6000),
  dismiss,
};

export default function ToastHost() {
  const [items, setItems] = useState<Toast[]>(current);
  useEffect(() => {
    listeners.add(setItems);
    return () => { listeners.delete(setItems); };
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="ikognit-toast-wrap" aria-live="polite" aria-atomic="false">
      {items.map(t => (
        <div key={t.id} className={`ikognit-toast ${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'}
          onClick={() => dismiss(t.id)}>
          {t.text}
        </div>
      ))}
    </div>
  );
}
